/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: ProjectCreationAgent.ts
 * Project: mle-client-n
 * Committed last: 2022/6/13 @ 1117
 * ---
 * Description:
 */

import {ColorModes, MeasureUnits, MetadataGroupKeys, MetadataKeys} from '../../common/enums';
import {Encoder, Metadata, MetaGroup, MetaKey} from '@revodigital/mpes';
import {measureUnitToMeasureUnit} from '../../common/measure-unit-utils';
import {mleDomainGqlClient} from '../../api/domain';
import {CREATE_LABEL} from '../../api/domain/mutations.gql';
import {ACTIVE_COMPANY, ACTIVE_RECIPE, ACTIVE_STATE_GROUP} from '../features/mylabel/RevisionSwitcherFeature';
import {Editor} from "../observables/Editor";
import {getProjectSoil} from "../../common/project";
import {objectToSelectItemsArray} from "../../common/utils";
import {ProjectDataFormValues} from "../../components/forms/ProjectDataForm";

export interface ProjectOptions {
  title: string;
  width: number;
  height: number;
  colorProfile: ColorModes;
  measureUnit: MeasureUnits;
}

export interface ActiveConfig {
  recipeId: number;
  companyId: number;
}

export interface ContextOptions {
  userId: number;
}

export interface ProjectCreationConfig {
  projectOptions: ProjectOptions;
  activeConfig?: ActiveConfig;
  contextOptions: ContextOptions;
}

/**
 * Agent per gestire la creazione di un nuovo progetto
 */
export class ProjectCreationAgent {
  /**
   * Gestisce la creazione del progetto. Restituisce l'id del progetto appena creato
   * @param config Configurazione per il nuovo progetto.
   * @returns l'id del nuovo progetto creato oppure -1 se ci sono stati errori
   */
  public static async createProject(config: ProjectCreationConfig): Promise<number> {
    try {
      const encoder = new Encoder();

      const m = new Metadata();
      m.addGroup(MetaGroup.init(MetadataGroupKeys.TEXT_BASED,
        [MetaKey.init<string>(MetadataKeys.MEASURE_UNIT,
          config.projectOptions.measureUnit)]));

      if (config.activeConfig) {
        const g = new MetaGroup({
          title: ACTIVE_STATE_GROUP,
          keys: [MetaKey.init(ACTIVE_RECIPE,
            config.activeConfig.recipeId), MetaKey.init(
            ACTIVE_COMPANY,
            config.activeConfig.companyId)],
          payload: {}
        });

        m.addGroup(g);
      }

      const encodedDocument = await encoder.encodeDocument({
        header: {
          title: config.projectOptions.title
        },
        canvasprops: {
          width: measureUnitToMeasureUnit(config.projectOptions.measureUnit,
            MeasureUnits.PX,
            config.projectOptions.width),
          height: measureUnitToMeasureUnit(config.projectOptions.measureUnit,
            MeasureUnits.PX,
            config.projectOptions.height),
          colorMode: parseInt(config.projectOptions.colorProfile),
          showGrid: false
        },
        content: {
          data: []
        },
        metadata: m,
      });

      const { data } = await mleDomainGqlClient.mutate({
        mutation: CREATE_LABEL,
        variables: {
          bodyContent: JSON.stringify(encodedDocument),
          title: config.projectOptions.title,
          userId: config.contextOptions.userId
        }
      });

      return data.addLabel.id;
    } catch (e) {
      console.error(e);
      return -1;
    }
  }

  /**
   * Restituisce la larghezza di default da utilizzare per il form
   * @param measureUnit Unità di misura da applicare
   * @param editor
   */
  static getDefaultWidth(measureUnit: MeasureUnits, editor?: Editor,): number {
    return editor ? measureUnitToMeasureUnit(
        MeasureUnits.PX,
        editor?.projectData.projectOptions.measureUnit as MeasureUnits,
        editor?.projectData.labelWidth as number) ?? (getProjectSoil(
        measureUnit) + 10) : (getProjectSoil(measureUnit) + 10);
  };

  /**
   * Restituisce l'altezza di default da utilizzare per la creazione di un progetto
   * @param measureUnit Unità di misura da applicare
   * @param editor
   */
  static getDefaultHeight(measureUnit: MeasureUnits, editor?: Editor): number {
    return editor ? measureUnitToMeasureUnit(
        MeasureUnits.PX,
        editor?.projectData.projectOptions.measureUnit as MeasureUnits,
        editor?.projectData.labelHeight as number) ?? (getProjectSoil(measureUnit) + 10) : (getProjectSoil(
        measureUnit) + 10);
  };


  /**
   * Restituisce la misura di default
   * @param editor
   */
  static getDefaultMeasureUnit(editor?: Editor) {
    return !editor ? objectToSelectItemsArray(MeasureUnits)[0] : objectToSelectItemsArray(
        MeasureUnits).filter(el => el.value === editor?.projectData.projectOptions.measureUnit)[0];
  };

  static getDefaultProjectData(): ProjectDataFormValues {
    return {
      width: this.getDefaultWidth(MeasureUnits.MM),
      height: this.getDefaultHeight(MeasureUnits.MM),
      title: '',
      measureUnit: MeasureUnits.MM,
      colorProfile: ColorModes.COLORE
    }
  }
}